import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DBSelectOption, DBIconModule, DBSpinnerModule, DBPanelModule, DBSelectModule, DBButtonModule} from '@db/db-core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AuthSteps, QueryParams} from 'src/app/core/interfaces/common';
import {CommonService} from 'src/app/shared/services/common.service';
import {environment} from 'src/environments/environment.generated';
import {HttpErrorResponse} from "@angular/common/http";
import {getAppThemeName} from "../../core/functions/common.function";
import {jwtDecode, JwtPayload} from "jwt-decode";
import * as uuid from 'uuid';
import {CommonModule} from "@angular/common";
import {KeepHtmlPipe} from "../../shared/pipes/keep-html.pipe";

@Component({
  selector: 'select-bcn-page',
  templateUrl: './select-bcn-page.component.html',
  styleUrls: ['./select-bcn-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DBIconModule,
    DBSpinnerModule,
    DBPanelModule,
    DBSelectModule,
    DBButtonModule,
    TranslateModule,
    KeepHtmlPipe
  ]
})
export class SelectBcnPageComponent implements OnInit {

  public authStepsValid: AuthSteps = {internalUser: false, idp: false, fkn: false};

  public appUrl: string = environment.appUrl;
  public internalUserAuthError = false;
  public fknItems: Array<DBSelectOption> = [];
  public fknSelectedValue = '';
  public fknSelectionError = false;
  public isLoading = true;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly appService: CommonService,
    private readonly translate: TranslateService,
    public cdp: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.checkUserAuthentication();
    sessionStorage.removeItem('lang');
    this.appService._languageData.subscribe(data => {
      this.translate.use(data.activeLang);
    });
  }

  checkUserAuthentication() {
    this.authStepsValid.internalUser = true;
    this.appService.changeInternalUserAuth(true);
    this.internalUserAuthError = false;
    setTimeout(() => {
      this.checkIdpAuth();
      this.cdp.detectChanges();
    }, 1000);
  }

  checkIdpAuth() {
    const receivedToken = this.activatedRoute.snapshot.queryParamMap.get('consent_request');
    if (this.isCRJWTInvalid(receivedToken || '')) {
      this.authStepsValid.idp = false;
      this.authStepsValid.internalUser = false;
      this.internalUserAuthError = true;
      this.authStepsValid.internalUser = false;
      this.router.navigateByUrl('error');
      return;
    }

    const jwtBody = this.readJWTBody(receivedToken || '');
    sessionStorage.setItem("x_request_id", jwtBody.claims.x_request_id || uuid.v4());
    console.log("No previous session, checking for new consent request...");
    this.authStepsValid.idp = false;
    this.startIDPAuthProcess(receivedToken);
  }

  startIDPAuthProcess(receivedToken: string | null) {
    if (receivedToken !== null) {
      console.log("New consent request found, continuing...");
      sessionStorage.removeItem('selectedFknKey');
      sessionStorage.removeItem('consentRequestId');
      sessionStorage.removeItem('isUserLoggedin');
      sessionStorage.removeItem('preselected');
    }
    setTimeout(() => {
      this.getResourceId(receivedToken);
    }, 200);
  }

  getResourceId(token: string | null) {
    this.appService.postRequestExternal(environment.endPoints.postForResource, [], [], undefined, token)
      .subscribe({
        next: (data: any) => {
          if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {

            console.log(`Redirecting to ${data.response.consentApprovalRedirectUri} with ${data.response.token} response JWT.`);
            this.appService.postToCIAM(data.response.consentApprovalRedirectUri, data.response.token);
            return;
          }
          if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BCN_SELECTION") {

            this.appService.changeIDPUserAuth(true);
            this.authStepsValid.idp = true;
            this.getFknItems(data.response.resourceId);
            return;
          }
          this.authStepsValid.internalUser = false;
          this.internalUserAuthError = true;
          this.authStepsValid.internalUser = false;

          console.log("Unable to initialize new flow, please retry.");
          this.router.navigateByUrl('error');
        },
        error: (errorResponse) => {
          this.authStepsValid.internalUser = false;
          this.internalUserAuthError = true;
          this.authStepsValid.internalUser = false;

          if (Array.isArray(errorResponse.error.pendingSteps) && errorResponse.error.pendingSteps.length > 0 && errorResponse.error.pendingSteps[0].step === "NOT_ENOUGH_ENTITLEMENTS") {
            console.error("Entitlements error:", errorResponse);
            this.router.navigateByUrl('entitlements-error');
          } else {
            console.error("Unable to initialize new flow, please retry.", errorResponse);
            this.router.navigateByUrl('error');
          }
        }
      })
  }

  getFknItems(resourceId: string) {
    const urlExtParams: Array<QueryParams> = [
      {paramName: 'requestTransactionId', paramValue: resourceId},
      {paramName: 'availableBranchCustomerNumbers', paramValue: 'availableBranchCustomerNumbers'}
    ];
    this.appService.getRequestExternal(environment.endPoints.getFkn, urlExtParams, []).subscribe({
      next: (data) => {
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {
          console.log(`Redirecting to ${data.response.consentApprovalRedirectUri} with ${data.response.token} response JWT.`);
          this.appService.postToCIAM(data.response.consentApprovalRedirectUri, data.response.token);
          return;
        }
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BCN_SELECTION_PROCESSING") {
          this.fknItems = data.response.availableBranchCustomerNumbers.map((fknKey: string) => {
            return {
              label: fknKey,
              value: fknKey,
            };
          });
          this.fknSelectionError = false;
          sessionStorage.setItem('consentRequestId', data.response.consentRequestId);
          if (this.fknItems.length === 1) {
            sessionStorage.setItem('preselected', 'true');
            this.fknSelectedValue = this.fknItems[0].value
            this.onFknSubmit();
            this.cdp.detectChanges();
            return;
          }
          this.isLoading = false;
          this.appService.changeShowHeaderAndFooter(true);
          this.fknSelectedValue = '';
          this.cdp.detectChanges();
          return;
        }

        this.fknSelectionError = true;
        this.cdp.detectChanges();
        this.router.navigateByUrl('error');
      },
      error: (error) => {
        console.error("Unable to get available customer branch numbers, please try again", error);

        this.internalUserAuthError = true;
        this.fknSelectionError = true;
        this.authStepsValid.internalUser = false;
        this.router.navigateByUrl('error');
      }
    });
  }

  onSelectFkn(e: Event) {
    if (e !== null && e.target !== null) {
      this.fknSelectedValue = (e.target as HTMLInputElement).value;
    }
  }

  onFknSubmit() {
    const urlExtParams: Array<QueryParams> = [
      {paramName: 'requestTransactionId', paramValue: sessionStorage.getItem('consentRequestId') as string}
    ];
    const requestBody = {
      selectedIdentity: [
        {
          identityType: 'BRANCH_CUSTOMER_NUMBER',
          identityValue: this.fknSelectedValue
        }
      ]
    }
    this.appService.patchRequestExternal(environment.endPoints.getFkn, requestBody, urlExtParams).subscribe({
      next: (data) => {
        if (data) {
          if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {
            console.log("Consent have been given. Redirecting...", data)
            sessionStorage.setItem('selectedFknKey', '');
            this.appService.changeIDPUserAuth(false);
            this.appService.postToCIAM(`${data.response.consentApprovalRedirectUri}`, data.response.token);
            return;
          }
          if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "CONSENT_DEFINITION_REQUEST") {
            sessionStorage.setItem('selectedFknKey', this.fknSelectedValue);
            this.appService.changeFknStatus(true);
            this.authStepsValid.fkn = true;

            this.appService._isIdpTokenSet.subscribe(data => {
              console.log(1, data)
            });
            this.appService._isInternalAuthSet.subscribe(data => {
              console.log(2, data)
            });
            this.appService._isFknKeySet.subscribe(data => {
              console.log(3, data)
            });

            setTimeout(() => {
              this.router.navigateByUrl('form');
            }, 1000);
            return;
          }
        }
        this.internalUserAuthError = true;
        this.fknSelectionError = true;
        this.authStepsValid.internalUser = false;
        this.router.navigateByUrl('error');
      },
      error: (err: HttpErrorResponse) => {
        this.internalUserAuthError = true;
        this.fknSelectionError = true;
        this.authStepsValid.internalUser = false;
        console.error("Patch request failed, please try again", err);
        this.router.navigateByUrl('error');
      }
    })
  }

  onCancel() {
    const queryParams: Array<QueryParams> = [{
      paramName: 'consentRequestId',
      paramValue: sessionStorage.getItem('consentRequestId') as string
    }];
    this.appService.deleteRequestExternal(environment.endPoints.cancelConsent, queryParams, []).subscribe({
      next: (data) => {
        if (Array.isArray(data.pendingSteps) && data.pendingSteps.length > 0 && data.pendingSteps[0].step === "BROWSER_REDIRECT_TO_IDP") {
          sessionStorage.removeItem('selectedFknKey');
          sessionStorage.removeItem('preselected');
          this.appService.changeIDPUserAuth(false);
          console.log(`Redirecting to ${data.response.consentApprovalRedirectUri} with ${data.response.token} response JWT.`);
          this.appService.postToCIAM(`${data.response.consentApprovalRedirectUri}`, data.response.token);
          console.debug('Consent canceled! With token: ', data);
          return;
        }

        sessionStorage.removeItem('selectedFknKey');
        sessionStorage.removeItem('preselected');
        this.internalUserAuthError = true;
        this.fknSelectionError = true;
        this.authStepsValid.internalUser = false;
        this.router.navigateByUrl('error');
      },
      error: (errorResponse: HttpErrorResponse) => {

        sessionStorage.removeItem('selectedFknKey');
        sessionStorage.removeItem('preselected');
        this.internalUserAuthError = true;
        this.fknSelectionError = true;
        this.authStepsValid.internalUser = false;
        this.router.navigateByUrl('error');
        console.error("Unable to cancel the flow", errorResponse)
      }
    });
  }

  isCRJWTInvalid(jwt: string) {
    try {
      const jwtPayload = jwtDecode<JwtPayload>(jwt);

      if (jwtPayload.aud === 'remoteConsent' && getAppThemeName() !== 'nb') {
        console.error("wrong aud provided", jwtPayload.aud);
        return true;
      }

      if (jwtPayload.aud === 'dbRemoteConsent' && getAppThemeName() !== 'db') {
        console.error("wrong aud provided", jwtPayload.aud);
        return true;
      }

      if (jwtPayload.exp && Date.now() > jwtPayload.exp * 1e3) {
        console.error("jwt expired by", Date.now() - jwtPayload.exp * 1e3);
        return true;
      }
    } catch (error) {
      console.error("Invalid token format. Please try again. Error: ", error);
      return true;
    }
    return false;
  }

  private readJWTBody(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replaceAll('-', '+').replaceAll('_', '/');
    const jsonPayload = decodeURIComponent(globalThis.atob(base64).split('').map(c => {
      const charCode = c.codePointAt(0) || 0;
      return '%' + ('00' + charCode.toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  protected readonly getAppThemeName = getAppThemeName;
  protected readonly sessionStorage = sessionStorage;
}
